<script setup lang="ts">
import { ImmersveError } from '@/common/ImmersveError';
import Error from '@/components/session/kyc/Error.vue';
import { getMessage } from '@/components/session/kyc/errorUtil';
import FrankieOneIDV from '@/components/session/kyc/idv/FrankieOneIDV.vue';
import SumsubIDV from '@/components/session/kyc/idv/SumsubIDV.vue';
import Loading from '@/components/ui/Loading.vue';
import type { KycProvider } from '@/models/KycSession';
import { wellKnownKycSessionRoutes } from '@/routes/wellKnownKycSessionRoutes';
import { useKycSessionStore } from '@/stores/kycSession.store';
import * as Sentry from '@sentry/vue';
import { type ComponentPublicInstance, onErrorCaptured, onMounted, ref, shallowRef } from 'vue';
import { useRouter } from 'vue-router';

function calculateIdvComponent(kycProvider?: KycProvider) {
  switch (kycProvider) {
    case 'frankieOne':
      return FrankieOneIDV;
    case 'sumsub':
      return SumsubIDV;
    default:
      throw ImmersveError.create({
        name: 'UnknownKycProvider',
        message: kycProvider ? `Unknown kycProvider ${kycProvider}` : 'kycProvider is undefined',
      });
  }
}

async function redirectToPending() {
  const router = useRouter();
  await router.push({ name: wellKnownKycSessionRoutes.PendingReview });
}

const loading = ref(true);
const idvComponent = shallowRef();
const inError = ref<boolean>(false);

onMounted(async () => {
  const kycSession = useKycSessionStore().kycSession;
  if (kycSession?.isCompleted()) {
    return await redirectToPending();
  }
  idvComponent.value = calculateIdvComponent(kycSession?.kycProvider);
  loading.value = false;
});

onErrorCaptured((e: { err: unknown; instance: ComponentPublicInstance | null; info: string }) => {
  const message = getMessage(e); // try to get any message
  Sentry.captureException(ImmersveError.create({ name: 'UnhandledIDVException', message }), {
    extra: { ...e },
  });
  inError.value = true;
  return false;
});
</script>

<template>
  <Error v-if="inError" />
  <Loading class="mx-auto mt-10 h-10 w-10" v-else-if="loading" />
  <Component :is="idvComponent" v-else />
</template>
